/**
 * /app/{slug}/me/exit — "Resignation" (persona round wave B).
 *
 * Employees could not resign in the app; an exit could only be opened by
 * HR. Here the employee:
 *   - submits a resignation (the last working day they ask for + reason)
 *     — their manager and HR are told; nothing changes until HR accepts;
 *   - sees it waiting for HR and can withdraw it;
 *   - once HR accepts, follows their exit: last working day and the exit
 *     checklist (read-only — the checklist is worked by HR, IT and their
 *     manager; nobody ticks their own exit's tasks).
 * Backend: /api/v1/me/resignation (handlers/resignations.go).
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { DoorOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useMyResignation,
  useSubmitResignation,
  useWithdrawResignation,
} from "@/hooks/use-resignations";
import { useOffboardingCycle } from "@/hooks/use-offboarding";
import { useDirectory } from "@/hooks/use-directory";
import {
  defaultRequestedLWD,
  myExitPanel,
  pendingResignation,
  RESIGNATION_STATUS_TEXT,
} from "@/lib/resignation";
import { fmtDate, taskStatusLabel, taskTypeLabel, todayISO } from "@/lib/offboarding-format";
import type { Resignation } from "@/lib/api/resignations";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/exit")({
  component: MyExitRoute,
});

function MyExitRoute() {
  const q = useMyResignation();
  const panel = myExitPanel(q.data?.open_exit, q.data?.items);
  const history = (q.data?.items ?? []).filter((r) => r.status !== "pending");

  return (
    <MeModuleShell
      title="Resignation"
      description="Resign, follow HR's answer, and see your exit once it starts."
    >
      <div className="space-y-6">
        {q.isLoading && <Skeleton className="h-40 w-full" />}
        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this page</AlertTitle>
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}
        {q.data && panel === "exit" && q.data.open_exit && (
          <ExitInProgress cycleId={q.data.open_exit.cycle_id} lastWorkingDay={q.data.open_exit.last_working_day} />
        )}
        {q.data && panel === "pending" && (
          <PendingCard resignation={pendingResignation(q.data.items) as Resignation} />
        )}
        {q.data && panel === "form" && <ResignForm />}
        {history.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Earlier Resignations</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y text-sm">
                {history.map((r) => (
                  <li key={r.id} className="space-y-0.5 py-2">
                    <p className="flex flex-wrap items-center gap-2">
                      <span>Resigned {fmtDate(r.resignation_date)}</span>
                      <Badge variant="outline">{RESIGNATION_STATUS_TEXT[r.status] ?? r.status}</Badge>
                    </p>
                    {r.decision_note && (
                      <p className="text-muted-foreground">Note from HR: {r.decision_note}</p>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </MeModuleShell>
  );
}

function ResignForm() {
  const today = todayISO();
  const [lwd, setLwd] = useState(defaultRequestedLWD(today));
  const [reason, setReason] = useState("");
  const submit = useSubmitResignation();
  const lwdInvalid = !lwd || lwd < today;

  async function onSubmit() {
    try {
      await submit.mutateAsync({ requested_last_working_day: lwd, reason: reason.trim() });
      toast.success("Resignation sent to HR. Your manager has been told.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <DoorOpen aria-hidden className="h-5 w-5 text-primary" />
          Resign
        </CardTitle>
        <CardDescription>
          HR and your manager are told. Nothing changes until HR accepts — you
          can withdraw it until then. Check your notice period in your
          appointment letter or the company policy.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid max-w-xs gap-1.5">
          <Label htmlFor="resign-lwd">Last working day you are asking for</Label>
          <Input
            id="resign-lwd"
            type="date"
            min={today}
            value={lwd}
            onChange={(e) => setLwd(e.target.value)}
            aria-invalid={lwdInvalid}
          />
          {lwdInvalid && <p className="text-xs text-destructive">Choose today or a later date.</p>}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="resign-reason">Reason (Optional)</Label>
          <Textarea
            id="resign-reason"
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Shared with HR and your manager"
          />
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={lwdInvalid || submit.isPending} className="gap-1.5">
              {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit Resignation
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Submit your resignation?</AlertDialogTitle>
              <AlertDialogDescription>
                HR and your manager will be told today, with {fmtDate(lwd)} as the
                last working day you are asking for. You can withdraw it until HR
                accepts it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Not Now</AlertDialogCancel>
              <AlertDialogAction onClick={() => void onSubmit()}>Submit Resignation</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function PendingCard({ resignation }: Readonly<{ resignation: Resignation }>) {
  const withdraw = useWithdrawResignation();

  async function onWithdraw() {
    try {
      await withdraw.mutateAsync(resignation.id);
      toast.success("Resignation withdrawn. HR and your manager have been told.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card data-testid="resignation-pending">
      <CardHeader>
        <CardTitle className="text-base">Your resignation is with HR</CardTitle>
        <CardDescription>
          HR will accept it (your exit then starts) or talk to you about it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <dt className="text-muted-foreground">Resigned On</dt>
          <dd>{fmtDate(resignation.resignation_date)}</dd>
          <dt className="text-muted-foreground">Last Working Day Asked For</dt>
          <dd>{fmtDate(resignation.requested_last_working_day)}</dd>
          {resignation.reason && (
            <>
              <dt className="text-muted-foreground">Reason</dt>
              <dd className="whitespace-pre-wrap break-words">{resignation.reason}</dd>
            </>
          )}
        </dl>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={withdraw.isPending}>
              Withdraw Resignation
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Withdraw your resignation?</AlertDialogTitle>
              <AlertDialogDescription>
                HR and your manager will be told you are staying.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep It</AlertDialogCancel>
              <AlertDialogAction onClick={() => void onWithdraw()}>Withdraw</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function ExitInProgress({ cycleId, lastWorkingDay }: Readonly<{ cycleId: number; lastWorkingDay: string }>) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const cycleQ = useOffboardingCycle(cycleId);
  const dir = useDirectory({ includeInactive: true });
  const tasks = cycleQ.data?.tasks ?? [];

  return (
    <Card data-testid="my-exit">
      <CardHeader>
        <CardTitle className="text-base">Your exit is in progress</CardTitle>
        <CardDescription>
          Last working day: {fmtDate(lastWorkingDay)}. Your relieving letter and
          full and final settlement will appear under{" "}
          <Link to="/app/$tenantSlug/me/letters" params={{ tenantSlug }} className="underline underline-offset-4">
            My letters
          </Link>
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="font-medium">Exit checklist</p>
        <p className="text-muted-foreground">
          HR, IT and your manager work through these. Talk to them about
          anything you need to hand over.
        </p>
        {cycleQ.isLoading && <Skeleton className="h-16 w-full" />}
        {cycleQ.isError && <p className="text-destructive">{toApiError(cycleQ.error).message}</p>}
        <ul className="divide-y rounded-md border">
          {tasks.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span>{taskTypeLabel(t.task_type)}</span>
              <span className="flex items-center gap-2 text-muted-foreground">
                {t.assignee_id ? dir.label(t.assignee_id, null, false) : "HR"}
                <Badge variant={t.status === "done" ? "default" : "outline"}>{taskStatusLabel(t.status)}</Badge>
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
